import * as React from 'react';
import classnames from 'classnames';
import { autoUpdate, useFloating } from '@floating-ui/react';
import { getAsset, getUrlSync, ICoreContext } from '@msdyn365-commerce/core';
import { PeriodicDiscountOfferType, ProductPrice } from '@msdyn365-commerce/retail-proxy';
import { getCartState, ICartState } from '@msdyn365-commerce/global-state';
import { getProductPageUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import { Button, format, Tooltip } from '@msdyn365-commerce-modules/utilities';
import { IProductListResources, ProductListProduct } from '../../product-list-component/product-list-component';
import { IProductListCompactComponentProps, ProductStates } from '../product-list-compact-component';
import QuantityBreakoffSelector from '../../common/quantity-breakoff-selector';
import QuantityInput from '../../common/quantity-input';
import { IProductError, SimpleProductExt } from '../../../shared-utils/types';
import { dividePriceBy, multiplyPriceBy } from '../../../shared-utils/price-utils';
import PriceComponent from '../../../themes/dlvry-adventureworks/views/components/price.component';
import { getLocale } from '../../product-list-component/locale';
import AddToOrderTemplateComponent from '../../../themes/dlvry-adventureworks/views/components/addtoordertemplate.component';
import { generateAddToOrderTemplateDialogResources, generateDuplicateItemsWhenAddingToOrderTemplateDialogResources, generateItemAddedToOrderTemplateDialogResources, generateOrderTemplateNameDialogResources } from '../../product-list-component/locale/add-to-order-template-resources';
import WishlistButton from './wishlist-button';
import { IToastViewProps, toast, toasts } from '../../toast/toast';
import PQueue from 'p-queue';
import { validateAlcoholLicenseAsync } from '../../../actions/validate-alcohol-license.action';
import { getDefaultBuyQuantity } from '../../../shared-utils/common-utils';
import ProductImage from './product-image';
import { humanizeUnitOfMeasure } from '../../unit-of-measure';
import { getNonBreakoffUOM, getTotalConversionText } from '../../../shared-utils/unit-conversion-utils';
import * as GTM from '../../../shared-utils/google-tag-manager/gtm';

export interface IProductLineProps extends IProductListCompactComponentProps {
    baseClassName: string;
    locale: ReturnType<typeof getLocale>;
    product: ProductListProduct;
    isDragOver?: 'over' | 'under' | undefined;
    isSelected?: boolean;
    min?: number;
    showMore: boolean;
    productStates: ProductStates;
    cartQueue: PQueue;
    error?: IProductError;
    setShowMore: (value: boolean) => void;
    onBreakoffChange: (product: ProductListProduct, breakoff: boolean) => void;
    onDragOver?: (recordId: number | undefined, overUnder: 'over' | 'under' | undefined) => void;
    onDropLine?: (event: React.DragEvent<HTMLTableRowElement>, recordId: number | undefined, overUnder: 'over' | 'under' | undefined) => void;
    onQuantityChange: (product: ProductListProduct, quantity: number) => void;
    setProductStates: React.Dispatch<React.SetStateAction<ProductStates>>;
    onIsSelectedChange?: (value: boolean) => void;
}

interface ICartCountProps {
    context: ICoreContext;
    cart: ICartState;
    product: SimpleProductExt;
    productId: number;
    inCartTooltipText?: string;
}

interface IPriceProps {
    className: string;
    context: ICoreContext;
    id: string;
    typeName: string;
    price: number | undefined;
    product: ProductListProduct;
    simpleProduct: SimpleProductExt | undefined;
    resources: IProductListResources;
    initialBreakoffEnabled: boolean | undefined;
    showHistoricalPrice?: boolean;
}

const CartCount = (props: ICartCountProps) => {
    const { context, cart, product, inCartTooltipText } = props;
    const [showCartCountTooltip, setShowCartCountTooltip] = React.useState<boolean>(false);
    const cartQuantity = cart?.cart.CartLines?.filter(x => x.ProductId === product.RecordId).reduce((acc, val) => acc + (val.Quantity || 0), 0);

    if (!cartQuantity) {
        return null;
    }

    const baseClassName = 'product-list-compact-component';
    const cartCountId = `cart-count-${product.RecordId}`;
    const humanizedUOM = humanizeUnitOfMeasure(context, product.DefaultUnitOfMeasure).toLowerCase();
    const tooltipString = inCartTooltipText || '{0} {1} i handlekurven';
    const tooltipText = format(tooltipString, cartQuantity, humanizedUOM);

    return (
        <div
            id={cartCountId}
            className={`${baseClassName}__cart-count`}
            onMouseOver={() => setShowCartCountTooltip(true)}
            onMouseOut={() => setShowCartCountTooltip(false)}
            onTouchStart={() => setShowCartCountTooltip(true)}
        >
            {cartQuantity}
            <Tooltip
                isOpen={showCartCountTooltip}
                target={`#${cartCountId}`}
                className='msc-cart-count-tooltip'
            >
                {tooltipText}
            </Tooltip>
        </div>
    );
};

const Price = (props: IPriceProps) => {
    const {
        className,
        context,
        id,
        typeName,
        price,
        product,
        simpleProduct,
        resources,
        initialBreakoffEnabled,
        showHistoricalPrice
    } = props;

    if (!context.request.user.isAuthenticated) {
        return null;
    }

    if (product.PriceFailed) {
        return <span className={`${className}__price__error`}>{resources.priceErrorText || 'Pris n/a'}</span>;
    }

    if (!price || !simpleProduct) {
        return <span className={`${className}__price__retrieving`}>-</span>;
    }

    let productPrice: ProductPrice | undefined;

    // If not use latest price if available otherwise whatever is set on the product
    productPrice = product.ProductPrice ? product.ProductPrice : {
        CustomerContextualPrice: price,
        BasePrice: product.BasePrice
    };

    if (!showHistoricalPrice && !initialBreakoffEnabled && simpleProduct.Behavior?.MultipleOfQuantity) {
        productPrice = multiplyPriceBy(productPrice, simpleProduct.Behavior.MultipleOfQuantity);
    }

    if (showHistoricalPrice && productPrice.CustomerContextualPrice) {
        if (initialBreakoffEnabled) {
            productPrice = dividePriceBy(productPrice, product.CurrentQuantity || 1);
        } else {
            productPrice = dividePriceBy(productPrice, product.CurrentQuantity || 1);
            productPrice = multiplyPriceBy(productPrice, simpleProduct.Behavior?.MultipleOfQuantity || 1);
        }
    }

    return (
        <PriceComponent
            context={context}
            data={{ price: productPrice }}
            id={id}
            typeName={typeName}
            discountPercentageSide='right'
            hideBeforePrice />
    );
};

const ProductLine = (props: IProductLineProps) => {
    const {
        mode = 'add-to-cart',
        moduleId,
        moduleType,
        resources,
        context,
        baseClassName,
        locale,
        allowMultiSelect,
        allowReorder,
        disableQuantityInput,
        isSelected,
        product,
        simpleProducts,
        customerInformation,
        productStates,
        showMore,
        showPrices,
        cartQueue,
        validatedProductAccess,
        setShowMore,
        onBreakoffChange,
        onQuantityChange,
        onBuyValidate,
        retrieveOrderTemplates,
        onUpdateOrderTemplates,
        setProductStates,
        onIsSelectedChange
    } = props;
    const { request } = context;
    const { user } = request;

    const [loading, setLoading] = React.useState(false);
    const { refs, floatingStyles } = useFloating({
        placement: 'bottom-end' as any,
        whileElementsMounted: autoUpdate,
        open: showMore,
        onOpenChange: setShowMore
    });
    const productStateKey = product.CartLine?.LineId || product.RecordId;

    React.useEffect(() => {
        if (productStates[productStateKey]?.quantity !== product.CurrentQuantity) {
            props.onQuantityChange(product, product.CurrentQuantity || 0);
        }
    }, [product.CurrentQuantity]);

    const productUrl = product ? getProductPageUrlSync(product.Name ?? '', product.RecordId, context.actionContext, undefined) : '';
    const price = showPrices !== false
        ? (product.Price && product.Price !== 0.00)
            ? product.Price : undefined
        : undefined;
    const simpleProduct = simpleProducts?.find((sp) => sp.RecordId === product.RecordId);
    const breakoff = productStates[productStateKey]?.breakoff || false;
    const quantity = productStates[productStateKey]?.quantity || 0;
    const isExpired = simpleProduct?.Behavior?.IsSaleAtPhysicalStoresAllowed === false || simpleProduct?.Behavior?.IsSaleAtSelfCheckoutRegistersAllowed === false;

    React.useEffect(() => {
        const initialBreakoff = Boolean(product.RecordId && props.breakoffProducts?.[product.RecordId]);

        if (breakoff !== initialBreakoff) {
            onBreakoffChange(product, initialBreakoff);
        }
    }, [props.breakoffProducts]);

    const addProductToCart = async (product: ProductListProduct, simpleProduct: SimpleProductExt | undefined) => {
        let quantity = productStates[productStateKey]?.quantity || 0;
        const breakoff = productStates[productStateKey]?.breakoff || false;

        if (!context || !product || !simpleProduct || !context.request.user.catalogId) {
            return;
        }

        // let cart = await getCartStateData(new GetCartStateDataInput(context.request.apiSettings, context.request), context.actionContext);
        let cart = await getCartState(context.actionContext);
        let retries = 0;

        while (cart.status === 'UPDATING') {
            if (retries++ > 10) {
                console.error('Cart is stuck in updating state, exiting...');
                alert('Could not add product to cart, please try again later.');
                return;
            }

            await new Promise((resolve) => setTimeout(resolve, 1000));
            // cart = await getCartStateData(new GetCartStateDataInput(context.request.apiSettings, context.request), context.actionContext);
            cart = await getCartState(context.actionContext);
        }

        let userHasAccess: boolean | undefined = validatedProductAccess?.find(x => x.RecordId === product.RecordId)?.HasAccess || true; // TODO: Remove 'true' default here to re-enable alcohol license check
        let validationError: string | undefined;

        if (userHasAccess === undefined) {
            const result = await validateAlcoholLicenseAsync(context.actionContext, {
                CatalogId: context.request.apiSettings.catalogId,
                ProductIds: [simpleProduct.RecordId]
            });
            userHasAccess = result.ValidationResult;
            validationError = locale.toastStrings.noAccessToProduct;
        }

        quantity = quantity || getDefaultBuyQuantity(simpleProduct, breakoff);

        if (userHasAccess && onBuyValidate) {
            const result = await onBuyValidate(
                product,
                quantity
            );
            userHasAccess = result.isValid;
            validationError = result.message;
        }

        if (userHasAccess !== true) {
            toasts.error(locale.toastStrings.errorHeader, validationError || 'Unknown error');
            return;
        }

        const addProductToCartInput = {
            product: simpleProduct,
            count: quantity,
            customPrice: product.Price
        };

        const cartLine = cart.cart.CartLines?.find(x => x.ProductId === simpleProduct.RecordId);

        try {
            const toAddCount = (!cartLine || !cartLine.LineId)
                ? addProductToCartInput.count
                : quantity;
            const result = (!cartLine || !cartLine.LineId)
                ? await cart.addProductToCart(addProductToCartInput)
                : await cart.updateCartLineQuantity({
                    cartLineId: cartLine.LineId,
                    newQuantity: (cartLine.Quantity || 0) + toAddCount
                });

            if (result.status === 'SUCCESS') {
                void GTM.addToCartAsync(product, toAddCount);

                const msg: IToastViewProps = {
                    text: format(locale.toastStrings.productsAddedToCart, toAddCount, simpleProduct.Name ?? ''),
                    type: 'success',
                    autoClose: 3000,
                    position: 'bottom-right',
                    canClose: true,
                    showProgress: true,
                    link: getUrlSync('cart', context.actionContext),
                    linkLabel: locale.toastStrings.goToCart
                };
                toast(msg);

                /* if (this.props.isSelected) {
                    this.props.onSelectCartHandler?.();
                } */

                setProductStates((value) => {
                    const newProductStates = { ...value };
                    newProductStates[product.RecordId].quantity = 0;
                    return newProductStates;
                });
            }
            else if (result.status === 'FAILED') {
                const text = result.validationResults && result.validationResults.length > 0
                    ? result.validationResults[0].LocalizedMessage!
                    : (result.errorDetails?.LocalizedMessage ?? 'Unknown error adding items to cart');
                toasts.error(locale.toastStrings.errorHeader, text);
            }
        }
        catch (err) {
            const error = err as unknown as any;
            const footer = error?.data?.CorrelationId ? `Correlation Id: ${error?.data?.CorrelationId}` : undefined;
            toasts.error(locale.toastStrings.errorHeader, error, { footer });
        }
    };
    const onBuyClick = async (
        product: ProductListProduct,
        simpleProduct: SimpleProductExt | undefined,
        setLoading: (value: boolean) => void
    ) => {
        setLoading(true);
        await cartQueue.add(() => addProductToCart(product, simpleProduct));
        setLoading(false);
    };
    const onDragStart = (e: React.DragEvent<HTMLDivElement>) => {
        e.stopPropagation();

        const offsetX = e.clientX - (e.target as Element).getBoundingClientRect().left;
        const offsetY = e.clientY - (e.target as Element).getBoundingClientRect().top;

        e.dataTransfer.setData('product', JSON.stringify({
            ProductName: product.Name || '',
            ProductRecordId: product.RecordId || 0,
            ProductUnitOfMeasure: product.DefaultUnitOfMeasure || ''
        }));
        e.dataTransfer.setDragImage((e.target as Element).parentElement as Element, offsetX, offsetY);
        e.dataTransfer.effectAllowed = 'copyMove';
    };

    const totalConversionText = getTotalConversionText(
        context,
        simpleProduct,
        props.unitConversions,
        quantity,
        breakoff
    );

    const hasMixAndMatchDiscount = product.ProductPrice?.DiscountLines
        ?.some(discount => discount.PeriodicDiscountTypeValue === PeriodicDiscountOfferType.MixAndMatch);
    const netPrice = product.ProductPrice && 'NetPrice' in product.ProductPrice ? (product.ProductPrice as any).NetPrice as number : undefined;
    const totalAmount = product.ProductPrice && 'TotalAmount' in product.ProductPrice ? (product.ProductPrice as any).TotalAmount as number : undefined;
    const totalPrice: ProductPrice = {
        CustomerContextualPrice: props.showHistoricalPrice
            ? price
            : hasMixAndMatchDiscount
                ? totalAmount || 0
                : (price || 0) * quantity,
        BasePrice: hasMixAndMatchDiscount ? netPrice : undefined,
        DiscountLines: hasMixAndMatchDiscount && netPrice ? product.ProductPrice?.DiscountLines : undefined
    };

    return (
        <tr
            key={`${product.RecordId}-${product.DefaultUnitOfMeasure}`}
            data-product-id={product.RecordId}
            className={classnames({
                'drag-over': props.isDragOver,
                [props.isDragOver || '']: props.isDragOver
            })}
            onDrop={(e) => {
                e.stopPropagation(); // Prevent onDrop handler on parent from firing
                props.onDropLine?.(e, product.RecordId, props.isDragOver);
            }}
            onDragOver={(e) => {
                e.stopPropagation();

                if (props.allowReorder && e.dataTransfer.types.includes('product')) {
                    e.preventDefault();

                    const rect = (e.currentTarget as Element).getBoundingClientRect();
                    const y = e.clientY - rect.top;
                    const halfHeight = rect.height / 2;
                    const overOrUnder: 'over' | 'under' = y > halfHeight ? 'under' : 'over';

                    props.onDragOver?.(product.RecordId, overOrUnder);
                }
            }}
        >
            {(allowMultiSelect || allowReorder) && (
                <td className='col-drag-select no-side-padding'>
                    <div>
                        {allowReorder && (
                            <div
                                className='drag-area'
                                draggable={props.allowReorder}
                                onDragStart={props.allowReorder ? onDragStart : undefined}
                                onDragEnd={() => props.onDragOver?.(undefined, undefined)}
                                onDragOver={allowReorder ? (e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                } : undefined}

                            >
                                <img className='drag-area__icon' src={getAsset('icons/drag-handle.svg', context.request)} />
                            </div>
                        )}

                        {allowMultiSelect && (
                            <label className='checkbox-container'>
                                <input
                                    aria-label='selectOneRowText'
                                    className='checkbox-input'
                                    type='checkbox'
                                    checked={isSelected}
                                    aria-checked={isSelected}
                                    onChange={(e) => onIsSelectedChange?.(e.target.checked)}
                                />
                                <span className='checkmark'/>
                            </label>
                        )}
                    </div>
                </td>
            )}
            <td className='no-padding'>
                <div style={{ position: 'relative' }}>
                    <a href={productUrl} title={product.Name}>
                        <ProductImage
                            {...props}
                            className='product-image'
                            heading={product.Name || ''}
                            product={product}
                            imageSettings={props.imageSettings}
                        />
                    </a>
                </div>
            </td>
            {/* <td className='bold uppercase grayed'>{product.Producer}</td> */}
            <td className='bold'>
                <div className={`${baseClassName}__product__producer`}>{product.Producer}</div>
                <a className={`${baseClassName}__product__link`} href={productUrl}>
                    {props.error && (
                        <span>
                            <i
                                className={`${baseClassName}__product__${props.error.Type}`}
                                title={props.error.Error} />
                            <span className='sr-only'>{props.error.Error}</span>
                        </span>
                    )}
                    <span>{product.Name}</span>
                </a>
            </td>
            <td>{product.ItemId}</td>
            <td className='quantity no-side-padding right-align'>
                {user.isAuthenticated && !props.readOnly && (
                    <div>
                        <QuantityBreakoffSelector
                            context={context}
                            disable={disableQuantityInput}
                            product={simpleProduct}
                            breakoff={breakoff}
                            quantity={quantity}
                            unitConversions={props.unitConversions}
                            onBreakoffChange={(breakoff) => onBreakoffChange(product, breakoff)}
                            onQuantityChange={(quantity) => onQuantityChange(product, quantity)}
                        />
                        <QuantityInput
                            context={context}
                            disable={disableQuantityInput}
                            product={simpleProduct}
                            breakoff={breakoff}
                            quantity={quantity}
                            min={props.min}
                            showOnlyIncrementDecrement={true}
                            showNonBreakoffAmount={false}
                            onQuantityChange={(quantity) => onQuantityChange(product, quantity)}
                            nonBreakoffUOM={getNonBreakoffUOM(simpleProduct, props.unitConversions)}
                            allowZero={props.allowZeroQuantity}
                        />
                        {totalConversionText && (
                            <span className={`${baseClassName}__total-conversion-text`} style={{ minWidth: 'unset', marginRight: 0 }}>
                                =
                            </span>
                        )}
                        <span className={`${baseClassName}__total-conversion-text`}>
                            {totalConversionText}
                        </span>
                    </div>
                )}
            </td>
            <td className='price-summary nowrap no-side-padding'>
                {!user.isAuthenticated && (
                    <div className={`${baseClassName}__price__not-logged-in`}>
                        {resources.priceNotLoggedInText || 'Logg inn for å se pris'}
                    </div>
                )}
                {user.isAuthenticated && (
                    <div>
                        <b>
                            <Price
                                className={`${baseClassName}__price`}
                                context={context}
                                id={moduleId}
                                typeName={moduleType}
                                resources={resources}
                                price={price}
                                product={product}
                                simpleProduct={simpleProduct}
                                initialBreakoffEnabled={breakoff}
                                showHistoricalPrice={props.showHistoricalPrice}
                            />
                        </b>
                    </div>
                )}
            </td>
            {props.showTotalPrices && (
                <td className='total-price'>
                    <div className={`${baseClassName}__total-price`} data-failed={props.product.PriceFailed}>
                        <b>
                            <PriceComponent
                                context={props.context}
                                data={{ price: totalPrice }}
                                id={props.moduleId}
                                typeName={props.moduleType}
                                hideBeforePrice />
                            {hasMixAndMatchDiscount && (
                                <div>
                                    <span className={`${baseClassName}__total-price__mix-and-match-discount`}>
                                        {/* locale.cartButton.mixAndMatchDiscountText || */ 'Mix & Match'}
                                    </span>
                                </div>
                            )}
                        </b>
                    </div>
                </td>
            )}
            {mode === 'add-to-cart' && (
                <td className='no-side-padding right-align'>
                    {user.isAuthenticated && props.cart && simpleProduct && (
                        <CartCount
                            context={context}
                            cart={props.cart}
                            product={simpleProduct}
                            productId={product.RecordId}
                            inCartTooltipText={'{0} {1} i handlekurven'}
                        />
                    )}

                    {user.isAuthenticated && (!props.readOnly && !isExpired) && (
                        <Button className={classnames(`${baseClassName}__buy-button`, { loading })} onClick={() => onBuyClick(product, simpleProduct, setLoading)}>
                            {resources.buyNowButtonText}
                        </Button>
                    )}

                    {user.isAuthenticated && isExpired && (
                        <Button className={classnames(`${baseClassName}__buy-button`)} disabled={true}>
                            {locale.cartButton.expired}
                        </Button>
                    )}
                </td>
            )}
            <td className='no-side-padding'>
                <div className={`${baseClassName}__right-actions`}>
                    {user.isAuthenticated && (
                        <>
                            <WishlistButton
                                context={context}
                                resources={resources}
                                wishlists={props.wishlists}
                                defaultWishlistName={props.defaultWishlistName}
                                locale={locale}
                                product={product}
                                onClick={() => setShowMore(false)}
                            />

                            {(retrieveOrderTemplates && (
                                <AddToOrderTemplateComponent
                                    className='order-template-button'
                                    addToOrderTemplateButtonText={locale.addToOrderTemplate.addToOrderTemplateButtonText || 'addToOrderTemplateButtonText'}
                                    addToOrderTemplateButtonTooltip={locale.addToOrderTemplate.addToOrderTemplateButtonTooltip || 'addToOrderTemplateButtonTooltip'}
                                    addToOrderTemplateDialogResources={generateAddToOrderTemplateDialogResources(locale)}
                                    createOrderTemplateDialogResources={generateOrderTemplateNameDialogResources(locale)}
                                    itemAddedToOrderTemplateDialogResources={generateItemAddedToOrderTemplateDialogResources(locale)}
                                    duplicateItemsWhenAddingToOrderTemplateDialogResources={generateDuplicateItemsWhenAddingToOrderTemplateDialogResources(locale)}
                                    context={context}
                                    id={moduleId}
                                    typeName={moduleType}
                                    disabled={!simpleProduct || !customerInformation}
                                    disableButton={!simpleProduct || !customerInformation}
                                    data={{
                                        partOfProductLists: product.PartOfProductLists,
                                        product: simpleProduct || {} as any,
                                        quantity: quantity || 1,
                                        customerInformation: customerInformation || { IsB2b: true } as any // Always pass 'IsB2b' so that it will always render (unless false from api)
                                    }}
                                    showButtonText
                                    onClick={() => setShowMore(false)}
                                    onSuccess={async (orderTemplate) => {
                                        await onUpdateOrderTemplates?.(product, orderTemplate);
                                        toast({
                                            text: `${product.Name}${locale.addToOrderTemplate.itemAddedToOrderTemplateHeaderMessageText}`,
                                            autoClose: 3000,
                                            canClose: true,
                                            position: 'bottom-right',
                                            type: 'success',
                                            showProgress: true
                                        });
                                    }}
                                    retrieveOrderTemplates={retrieveOrderTemplates}
                                />
                            ))}

                            {props.onProductRemove && (
                                <button
                                    className={classnames(`${baseClassName}__btn-remove`)}
                                    title={props.resources.removeButtonText || 'Fjern'}
                                    onClick={async () => {
                                        setShowMore(false);
                                        await props.onProductRemove?.(product);
                                    }}
                                >
                                    <svg width="16px" height="16px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14.7404 9L14.3942 18M9.60577 18L9.25962 9M19.2276 5.79057C19.5696 5.84221 19.9104 5.89747 20.25 5.95629M19.2276 5.79057L18.1598 19.6726C18.0696 20.8448 17.0921 21.75 15.9164 21.75H8.08357C6.90786 21.75 5.93037 20.8448 5.8402 19.6726L4.77235 5.79057M19.2276 5.79057C18.0812 5.61744 16.9215 5.48485 15.75 5.39432M3.75 5.95629C4.08957 5.89747 4.43037 5.84221 4.77235 5.79057M4.77235 5.79057C5.91878 5.61744 7.07849 5.48485 8.25 5.39432M15.75 5.39432V4.47819C15.75 3.29882 14.8393 2.31423 13.6606 2.27652C13.1092 2.25889 12.5556 2.25 12 2.25C11.4444 2.25 10.8908 2.25889 10.3394 2.27652C9.16065 2.31423 8.25 3.29882 8.25 4.47819V5.39432M15.75 5.39432C14.5126 5.2987 13.262 5.25 12 5.25C10.738 5.25 9.48744 5.2987 8.25 5.39432" stroke="#0F172A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                                    </svg>
                                </button>
                            )}

                            {/* <button
                                ref={refs.setReference as any}
                                className={classnames(`${baseClassName}__more-button`, { open: showMore })}
                                onClick={() => setShowMore(!showMore)}
                                title='Mer'
                            >
                                <svg width="12.98px" height="12.98px" version="1.1" viewBox="0 0 1200 1200" xmlns="http://www.w3.org/2000/svg">
                                    <path d="m603.12 51.359c66 0 120 54 120 120s-54 120-120 120-120-54-120-120 54-120 120-120zm-120 960c0 66 54 120 120 120s120-54 120-120-54-120-120-120-120 54-120 120zm0-420c0 66 54 120 120 120s120-54 120-120-54-120-120-120-120 54-120 120z"/>
                                </svg>
                            </button> */}
                        </>
                    )}
                </div>

                {(
                    <div
                        ref={refs.setFloating}
                        className={classnames(`${baseClassName}__more-popup`, { open: showMore })}
                        style={floatingStyles}
                    >
                    </div>
                )}
            </td>
        </tr>
    );
};

export default ProductLine;
